import { Component, JSX, createEffect, onCleanup, onMount, useContext } from "solid-js"
import { OrthographicCamera, WebGLRenderTarget } from "three"
import { createCanvasElement } from "../../lib/canvas/create_canvas"
import { loseWebGL2Context } from "../../lib/canvas/loseWebGL2Context"
import { Engine } from "../../lib/core/engine/Engine.type"
import getEntityRenderHeight from "../../lib/core/entity/getEntityRenderHeight"
import findByPosition from "../../lib/core/findByPosition"
import type { TilePositionXY } from "../../lib/core/tile_position_xy/TilePositionXY.type"
import { cos, max, sin } from "../../lib/core/util/math/Math"
import setDuskAttributes from "../../lib/dom/setDuskAttribute"
import { VERTICES_PER_TILE } from "../../lib/three/consts"
import { getEngineThreeMainRenderer } from "../../lib/three/fn/getSetEngineThreeMainRenderer"
import useMainScene from "../../lib/three/fn/useMainScene"
import { onCleanupDispose } from "../../rx/cleanup/onCleanupDispose"
import { createSharedTileGraphNodeCollectionMemo } from "../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import EngineContext from "../EngineContext"

interface Props {
  position: TilePositionXY,
  w: number,
  h: number,
  style?: JSX.CSSProperties,
}

/**
 * @deprecated some details do not render correctly, we'll use static icon images instead for now.
 * 
 * based on SingleTileCanvas
 */
const PositionIcon3D: Component<Props> = (props: Props) => {
  // console.log('PositionIcon3D', props)
  const engine: Engine = useContext(EngineContext)

  const getTpList = createSharedTileGraphNodeCollectionMemo(engine)

  const scene = useMainScene(engine)
  const mainRenderer = getEngineThreeMainRenderer(engine)

  const canvasElem = createCanvasElement()
  setDuskAttributes(canvasElem, 'PositionIcon3D')
  canvasElem.classList.add('sprite-icon')
  canvasElem.classList.add('stack-icon')

  // eslint-disable-next-line solid/reactivity
  const targetW = props.w
  // eslint-disable-next-line solid/reactivity
  const targetH = props.h

  canvasElem.width = targetW
  canvasElem.height = targetH

  const aspect = canvasElem.width / canvasElem.height
  const near = 0.1
  const far = 1000

  const frustumSize = 40 // Adjust based on your scene scale
  const left = (-frustumSize * aspect) / 2
  const right = (frustumSize * aspect) / 2
  const top = frustumSize / 2
  const bottom = -frustumSize / 2
  const camera = new OrthographicCamera(left, right, top, bottom, near, far)

  // Create a render target for offscreen rendering
  const renderTarget = new WebGLRenderTarget(targetW, targetH)
  onCleanupDispose(renderTarget)

  let lookAtX = 0
  let lookAtY = 0
  let lookAtZ = 0
  createEffect(() => {
    const tpList = getTpList()
    createEffect(() => {
      // eslint-disable-next-line solid/reactivity
      const { position } = props
      if (position) {
        const { x, y } = position
        // const stack = getEntityListAtXYMap().get(coord(x, y)) || []
        const node = findByPosition(tpList, position)
        if (node) {
          const stack = node.ents()
          lookAtX = x * VERTICES_PER_TILE
          lookAtY = max(0, getEntityRenderHeight(stack))
          lookAtZ = y * VERTICES_PER_TILE
          // console.log('PositionIcon3D.lookAtX', {
          //   x: lookAtX,
          //   y: lookAtY,
          //   z: lookAtZ,
          // })
        }
      }
    })
  })

  let angle: number = 0
  const radius = VERTICES_PER_TILE * 0.5
  
  function render() {
    if (!mainRenderer) return
    
    camera.rotation.x += 0.01
    angle += 0.0025
    camera.position.x = lookAtX + (radius * cos(angle)) // Update camera position (x)
    camera.position.z = lookAtZ + (radius * sin(angle)) // Update camera position (z)
    camera.lookAt(lookAtX, lookAtY, lookAtZ) // Ensure the camera looks at the center
    camera.position.y = lookAtY + radius

    // Render to offscreen target
    mainRenderer.setRenderTarget(renderTarget)
    mainRenderer.render(scene, camera)
    
    // Copy the result to our canvas
    const ctx = canvasElem.getContext('2d')
    if (ctx) {
      // Read pixels from the render target
      const pixels = new Uint8Array(targetW * targetH * 4)
      mainRenderer.readRenderTargetPixels(renderTarget, 0, 0, targetW, targetH, pixels)
      
      // Create ImageData and put it on the canvas
      const imageData = new ImageData(new Uint8ClampedArray(pixels), targetW, targetH)
      ctx.putImageData(imageData, 0, 0)
    }
    
    // Restore main renderer
    mainRenderer.setRenderTarget(null)
    
    animationId = requestAnimationFrame(render)
  }

  let animationId: number = 0
  onMount(() => {
    animationId = requestAnimationFrame(render)
  })

  onCleanup(() => {
    // console.log('PositionIcon3D.cleanup')
    cancelAnimationFrame(animationId)
    loseWebGL2Context(canvasElem)
    canvasElem.remove()
  })

  return canvasElem
}

export default PositionIcon3D
